<script lang="ts" setup>
import type { AuthMe } from '#auth/composables/useAuth'
import type { ContactTypeEnumType, NotificationTypeEnumType } from '@forgd/shared/enums'
import { useNotificationsStore } from '#core/services/notifications/state/useNotificationsStore'
import { formatValue } from '@forgd/formatter'
import { NoData } from '@forgd/ui-kit/blocks/empty-states'
import { Button } from '@forgd/ui-kit/button'
import { Popover, PopoverContent, PopoverTrigger } from '@forgd/ui-kit/popover'
import { BellIcon, CheckIcon, Cog6ToothIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'

const auth = useAuth()
const notificationStore = useNotificationsStore()
const { me } = storeToRefs(auth)
const { notifications } = storeToRefs(notificationStore)

function formatMessage(template: string, values: Record<string, any>): string {
  return template.replace(/\[([^\]|]+)(?:\|([^\]]+))?\]/g, (_, key, formatOption) => {
    const value = values[key]

    if (formatOption) {
      return formatValue(value, formatOption)
    }

    if (value === undefined) {
      return `[${key}]` // Keep placeholder if missing
    }
    return value
  })
}

const NotificationTemplatesMap = {
  rfq_created: '🙌 Your RFQ was created successfully.',
  rfq_approved: '📣 There is a new RFQ in Forgd! [projectName] is looking for Market Makers. Send your proposals before [date|date].',
  rfq_canceled: '🛑 [projectName] has cancelled its RFQ in round [round]. All submitted quotes have been voided.',
  quote_submitted: '✅ [projectName]`s RFQ has received your submitted quote.',
  quote_canceled: '❌ One of your submitted quotes to [projectName] was cancelled.',
  quote_edited: '✏️ One of your submitted quotes to [projectName]`s RFQ was edited.',
  rfq_competition_update: '[projectName] has received [quotesQty] quotes from Market Makers in the past [hoursLeft] hours, and currently has [loanCallQuotesQty] Loan + Call Option quotes, and [retainerWorkingCapitalQuotesQty] Retainer + Working Capital quotes.',
  rfq_competition_update_1h: '[projectName] has received [quotesQty] quotes from Market Makers in the past [hoursLeft] hour, and currently has [loanCallQuotesQty] Loan + Call Option quotes, and [retainerWorkingCapitalQuotesQty] Retainer + Working Capital quotes.',
  rfq_time_remaining_24h: '⏳ “Accepting Quotes” window for [projectName]’s RFQ-Round [round] will close in 24 hours. You still have time to fine tune your proposals.',
  rfq_time_remaining_1h: '🚨 Last Call. “Accepting Quotes” window for [projectName]’s RFQ-Round [round] will close in 1 hour.',
  rfq_round_1_quotes_accepted_selected_market_maker: '🎉 Congratulations! [projectName] has selected one or more of your Round 1 quotes.',
  rfq_round_1_quotes_accepted_other_market_maker: '❌ [projectName]`s RFQ has not selected your quotes.',
  rfq_round_1_quotes_requoted_selected_market_maker: '🎉 Congratulations! [projectName] has selected one or more of your quotes for a requote. See the RFQ outcome, and prepare your new proposals.',
  rfq_round_1_quotes_requoted_other_market_maker: '👀 [projectName]’s RFQ has moved to the next phase. Unfortunately none of your proposals were selected on Round 1. See the outcome and learn about the selection preferences.',
  rfq_round_2_quotes_accepted_selected_market_maker: '🎉 Congratulations! [projectName] has selected one or more of your Round 2 quotes. See the RFQ outcome, and prepare your new proposals.',
  rfq_round_2_quotes_accepted_other_market_maker: '❌ [projectName]`s RFQ has not selected your quotes.',
  missing_data_drops: `❗️We didn't receive any daily data during the last three days.`,
  contact_sent: 'New [type] requested',
  api_key_expiring: 'Your API Key [name] will expire [date]. Extend the expiration date to prevent reports using this key to be paused.',
  api_key_expired: 'Your API Key [name] has expired. Reports submitted using this key cannot be recorded. To resume data collection, please extend the key’s expiration date.',
  api_key_deleted: 'Your API Key [name] expired on [date] and has been automatically deleted. Create a new key to resume data collection.',
} as const satisfies Record<NotificationTypeEnumType, string>

const notificationDataSource = computed(() => {
  return notifications.value.map((n) => {
    return {
      id: n.id,
      createdAt: n.createdAt,
      read: n.read,
      text: formatMessage(NotificationTemplatesMap[n.type], n.data),
    }
  })
})
const contactAddresses = ref<{ type: ContactTypeEnumType, address: string, enabled: boolean }[]>([])
const unreadQty = computed(() => notificationDataSource.value.filter(n => !n.read).length)
const noData = computed(() => notificationDataSource.value.length === 0)
const chipText = computed(() => {
  if (unreadQty.value === 0) {
    return ''
  }
  return unreadQty.value > 9 ? '9+' : unreadQty.value.toString()
})

function onClose(v: boolean) {
  if (!v) {
    notificationStore.markAllAsRead()
  }
}

watch(me, (val: AuthMe | null) => {
  if (val) {
    contactAddresses.value = []
    if (val.email) {
      contactAddresses.value.push({ type: 'email', address: val.email, enabled: true })
    }
    if ('telegram' in val && val.telegram) {
      contactAddresses.value.push({ type: 'telegram', address: val.telegram, enabled: true })
    }
  }
}, { deep: true, immediate: true })

onMounted(() => {
  notificationStore.startPolling()
})

onUnmounted(() => {
  notificationStore.stopPolling()
})
</script>

<template>
  <div data-el="CoreAppNotifications">
    <Popover @update:open="onClose">
      <div class="relative inline-flex">
        <PopoverTrigger as-child>
          <Button variant="outline" size="sm" icon-only aria-label="Notifications">
            <BellIcon aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <span
          v-if="unreadQty"
          class="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 h-[1.125rem] min-w-[1rem] rounded-full bg-brand-300 ring-1 ring-white flex items-center justify-center text-white text-xs font-normal whitespace-nowrap p-1 pointer-events-none"
        >
          {{ chipText }}
        </span>
      </div>
      <PopoverContent class="w-87.5 p-0">
        <div class="flex flex-col rounded-lg divide-y divide-solid divide-border-default">
          <div class="flex items-center justify-between px-3 py-4">
            <div class="flex items-center gap-2">
              <BellIcon class="size-5 shrink-0" aria-hidden="true" />
              <span class="text-xs">
                Notifications
              </span>
            </div>
            <span class="text-xs" :class="unreadQty === 0 ? 'text-disabled/30' : 'text-[#3F4D62]'">
              {{ unreadQty }} unread
            </span>
          </div>
          <NoData
            v-if="noData"
            title="You are all caught up!"
            message="Stay tuned for future notifications."
            class="min-h-40"
          />
          <div v-else class="flex flex-col min-h-40 max-h-101 pl-5 py-2 gap-2 overflow-y-auto">
            <div
              v-for="notification in notificationDataSource"
              :key="notification.id"
              class="relative flex flex-col gap-1 border-l-4 text-xs pl-2 pr-6"
              :class="notification.read ? 'border-[#8E8E7F]' : 'border-accent'"
            >
              <span class="font-semibold">
                {{ notification.text }}
              </span>
              <span class="text-gray-600">
                {{ formatValue(notification.createdAt, 'time:relative,3') }}
              </span>
              <Button
                size="xs"
                variant="ghost"
                icon-only
                aria-label="Delete notification"
                class="absolute right-1"
                @click="notificationStore.deleteNotification(notification.id)"
              >
                <XMarkIcon aria-hidden="true" />
              </Button>
            </div>
          </div>
          <div class="flex items-center p-2 justify-around">
            <Button variant="outline" :disabled="noData" size="sm" class="w-35" @click="notificationStore.deleteAllNotifications">
              Clear All
              <TrashIcon aria-hidden="true" />
            </Button>
            <Button variant="outline" :disabled="noData" size="sm" @click="notificationStore.markAllAsRead">
              Mark All As Read
              <CheckIcon aria-hidden="true" />
            </Button>
            <Button as-child variant="outline" size="sm" icon-only>
              <NuxtLink to="/settings" aria-label="Settings">
                <Cog6ToothIcon aria-hidden="true" />
              </NuxtLink>
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>
